/**
 * CropModal — manual square crop over the ORIGINAL image.
 *
 * Works in original-pixel space: region = {sx, sy, srcSize}. The preview is
 * rendered with percentage offsets (so no px measurement needed), and drag
 * converts pointer px -> original px via the stage width. Output {x,y,size} is
 * sent to /api/wall/crop, which builds a Cloudinary c_crop transform — so the
 * crop the user sees is exactly what gets printed. Pointer events => mobile drag.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { frameWebp, frameByName } from "./data";

const MAX_ZOOM = 3;

export default function CropModal({ open, src, frameName, sw = 74, bw = false, initial, onSave, onClose }) {
  const stageRef = useRef(null);
  const [dims, setDims] = useState(null); // {W,H}
  const [region, setRegion] = useState(null); // {sx,sy,srcSize}
  const [saving, setSaving] = useState(false);
  const drag = useRef(null);

  useEffect(() => {
    if (!open || !src) return undefined;
    setDims(null);
    setRegion(null);
    let cancelled = false;
    const im = new Image();
    // No crossOrigin: we only read dimensions, and the S3 originals don't send
    // CORS headers -> an anonymous request would fail to load and stay hidden.
    im.onload = () => {
      if (cancelled) return;
      const W = im.naturalWidth, H = im.naturalHeight;
      setDims({ W, H });
      const base = Math.min(W, H);
      if (initial && initial.size) {
        setRegion({ sx: initial.x, sy: initial.y, srcSize: Math.min(initial.size, base) });
      } else {
        setRegion({ sx: (W - base) / 2, sy: (H - base) * 0.33, srcSize: base });
      }
    };
    im.src = src;
    return () => { cancelled = true; };
  }, [open, src]); // eslint-disable-line react-hooks/exhaustive-deps

  const clamp = useCallback((r) => {
    if (!dims) return r;
    const size = Math.max(dims.W * 0.15, Math.min(Math.min(dims.W, dims.H), r.srcSize));
    return {
      srcSize: size,
      sx: Math.max(0, Math.min(dims.W - size, r.sx)),
      sy: Math.max(0, Math.min(dims.H - size, r.sy)),
    };
  }, [dims]);

  const onPointerDown = (e) => {
    if (!region || !stageRef.current) return;
    const w = stageRef.current.getBoundingClientRect().width || 1;
    drag.current = { x: e.clientX, y: e.clientY, sx: region.sx, sy: region.sy, scale: region.srcSize / w };
    try { stageRef.current.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setRegion((r) => clamp({ ...r, sx: d.sx - (e.clientX - d.x) * d.scale, sy: d.sy - (e.clientY - d.y) * d.scale }));
  };
  const onPointerUp = () => { drag.current = null; };

  const onZoom = (e) => {
    const cz = +e.target.value;
    setRegion((r) => {
      if (!dims) return r;
      const base = Math.min(dims.W, dims.H);
      const newSize = base / cz;
      const cx = r.sx + r.srcSize / 2, cy = r.sy + r.srcSize / 2;
      return clamp({ srcSize: newSize, sx: cx - newSize / 2, sy: cy - newSize / 2 });
    });
  };

  const save = async () => {
    if (!region) return;
    setSaving(true);
    try {
      await onSave({ x: Math.round(region.sx), y: Math.round(region.sy), size: Math.round(region.srcSize) });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const scale = region ? region.srcSize : 1;
  const imgStyle = region && dims
    ? {
        width: (dims.W / scale) * 100 + "%",
        height: (dims.H / scale) * 100 + "%",
        left: (-region.sx / scale) * 100 + "%",
        top: (-region.sy / scale) * 100 + "%",
      }
    : { display: "none" };
  const czValue = region && dims ? Math.min(dims.W, dims.H) / region.srcSize : 1;

  // Mirror the wall's per-frame compositing (see WallScene .grid.flat rules):
  // flat/matte frames keep the frame behind and the window sized to the opening;
  // shadowed frames overlay the frame on top with a 2% photo bleed so there's no gap.
  const flat = frameByName(frameName).shadow === false;
  const winSize = flat ? `${sw}%` : `calc(${sw}% + 2%)`;

  return (
    <>
      <div className="wallp-crop-ov open" onClick={onClose} />
      <div className="wallp-crop open">
        <div className="crop-head">
          <b>התאמת חיתוך</b>
          <button type="button" className="crop-x" onClick={onClose} aria-label="סגירה">×</button>
        </div>
        {/* Mirror the wall's compositing exactly (WallScene .fr/.slot/.fr::after):
            the photo sits in a clipped window and the frame webp is layered ON TOP,
            so the border always contains the photo — it can never spill past the
            frame no matter how the crop is dragged/zoomed. Flat/matte frames read
            without an opaque border, so (like the wall) they keep the frame behind. */}
        <div className={`crop-view${flat ? " flat" : ""}`}>
          <div
            className="crop-win"
            ref={stageRef}
            style={{ width: winSize, height: winSize }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {src ? <img src={src} alt="" style={{ ...imgStyle, filter: bw ? "grayscale(1)" : "none" }} draggable={false} /> : null}
          </div>
          <div className="crop-frame" style={{ "--cframe": `url('${frameWebp(frameName)}')` }} aria-hidden="true" />
        </div>
        <div className="crop-hint">גררו למיקום · החליקו לזום</div>
        <input type="range" className="crop-zoom" min="1" max={MAX_ZOOM} step="0.01" value={czValue} onChange={onZoom} />
        <button type="button" className="crop-save" onClick={save} disabled={saving || !region}>
          {saving ? "שומר…" : "שמירה"}
        </button>
      </div>
    </>
  );
}
